<template>
  <div class="club flex flex-col gap-10 pb-16 mt-6">
    <div v-if="loading" class="club-loading">
      <spinner />
    </div>

    <template v-else>
      <!-- Modo assinatura: o card diz o que é e o estado da assinatura (o preço só aparece no paywall). -->
      <section v-if="isSubscription" class="club-hero">
        <div class="club-circle club-circle--lg">
          <feather-icon icon="GiftIcon" svgClasses="w-7 h-7" />
        </div>
        <div class="club-hero__text">
          <div class="club-hero__title">
            <h3>Clube de Benefícios Recrutei</h3>
            <span v-if="subscriptionStatus === 'active' || subscriptionStatus === 'past_due'" class="club-badge">Assinante</span>
            <span v-else-if="subscriptionStatus === 'ended'" class="club-badge club-badge--muted">Assinatura encerrada</span>
            <span v-else-if="trialStatus === 'ended'" class="club-badge club-badge--muted">Teste encerrado</span>
          </div>
          <p v-if="active">
            Seus descontos estão liberados. Escolha uma oferta abaixo ou abra o clube para navegar por lá.
          </p>
          <p v-else-if="trialStatus === 'ended'">
            Seu teste grátis terminou em {{ formatDate(trial.ends_at) }}. Para continuar usando os descontos de mais de
            490 parceiros, assine o clube. Você cancela quando quiser.
          </p>
          <p v-else-if="subscriptionStatus === 'ended'">
            Sua assinatura terminou em {{ formatDate(subscription.current_period_end) }} e o acesso ao site do clube foi
            encerrado. As ofertas continuam aqui para quando você quiser voltar.
          </p>
          <p v-else>
            Procurar emprego também pesa no bolso. Com o Clube de Benefícios Recrutei você tem descontos em cursos,
            idiomas, terapia, cinema e lojas de mais de 490 parceiros. Um único desconto já pode valer mais que a
            mensalidade, e você cancela quando quiser.
          </p>
          <p class="club-note">
            <feather-icon icon="InfoIcon" svgClasses="w-4 h-4" />
            <span>
              O Clube de Benefícios é da Recrutei, a plataforma onde você se candidata. Não é um benefício das empresas
              que anunciam vagas aqui, e assinar ou não assinar não muda nada nos seus processos seletivos.
            </span>
          </p>
          <p v-if="subscriptionStatus === 'past_due'" class="club-error">
            Não conseguimos cobrar seu cartão. Atualize o pagamento em Gerenciar assinatura para não perder o acesso.
          </p>
          <div v-if="active || consented" class="club-hero__actions">
            <template v-if="active">
              <button class="club-btn" :disabled="opening" @click="openClub(null)">
                Abrir o site do clube
                <feather-icon icon="ExternalLinkIcon" svgClasses="w-5 h-5" />
              </button>
              <span class="club-hero__meta">
                <feather-icon icon="ClockIcon" svgClasses="w-4 h-4" />
                {{ subscription.cancel_at_period_end ? "Termina em" : "Renova em" }}
                {{ formatDate(subscription.current_period_end) }} ·
                <button class="club-link" :disabled="portalLoading" @click="openPortal">Gerenciar assinatura</button>
              </span>
            </template>
            <template v-else-if="consented">
              <button class="club-btn club-btn--primary" @click="openPaywall(null)">
                {{ subscriptionStatus === "ended" ? "Assinar de novo" : "Assinar o clube" }}
              </button>
              <span class="club-hero__meta">
                Cancele quando quiser.<template v-if="subscriptionStatus !== 'ended'"> Escolha uma oferta abaixo para ver como funciona.</template>
              </span>
            </template>
          </div>
        </div>
      </section>

      <!-- Teste grátis: um clique mostra as ofertas; a primeira ida ao site do clube começa os dias -->
      <section v-else-if="isTrial" class="club-hero">
        <div class="club-circle club-circle--lg">
          <feather-icon icon="GiftIcon" svgClasses="w-7 h-7" />
        </div>
        <div class="club-hero__text">
          <div class="club-hero__title">
            <h3>Clube de Benefícios Recrutei</h3>
            <span v-if="trialStatus === 'active'" class="club-badge">Grátis até {{ formatDate(trial.ends_at) }}</span>
            <span v-else class="club-badge">{{ trialDays }} dias grátis</span>
          </div>
          <p v-if="trialStatus === 'active'">
            Seus descontos estão liberados até {{ formatDate(trial.ends_at) }}. Escolha uma oferta abaixo ou abra o
            clube para navegar por lá. Depois disso, se quiser continuar, é só assinar.
          </p>
          <p v-else>
            Procurar emprego também pesa no bolso. Use de graça, por {{ trialDays }} dias, descontos em cursos, idiomas,
            terapia, cinema e lojas de mais de 490 parceiros. Os {{ trialDays }} dias começam quando você usa a primeira
            oferta.
          </p>
          <p class="club-note">
            <feather-icon icon="InfoIcon" svgClasses="w-4 h-4" />
            <span>
              O Clube de Benefícios é da Recrutei, a plataforma onde você se candidata. Não é um benefício das empresas
              que anunciam vagas aqui, e usar ou não usar não muda nada nos seus processos seletivos.
            </span>
          </p>
          <p v-if="error && !consented" class="club-error">{{ error }}</p>
          <div class="club-hero__actions">
            <!-- O clique é o aceite: o texto logo abaixo diz o que ele autoriza. -->
            <template v-if="!consented">
              <button class="club-btn club-btn--primary" :disabled="activating" @click="activate">
                {{ activating ? "Abrindo..." : "Ver as ofertas" }}
              </button>
            </template>
            <template v-else-if="trialStatus === 'active'">
              <button class="club-btn" :disabled="opening" @click="openClub(null)">
                Abrir o site do clube
                <feather-icon icon="ExternalLinkIcon" svgClasses="w-5 h-5" />
              </button>
              <span class="club-hero__meta">
                <button class="club-link" @click="openPaywall(null)">Assinar o clube</button>
              </span>
            </template>
            <span v-else class="club-hero__meta">Escolha uma oferta abaixo para começar.</span>
          </div>
          <p v-if="!consented" class="club-consent__small">
            Ao clicar, você concorda com o uso da sua cidade e da sua área de interesse para sugerir ofertas e, quando
            usar a primeira oferta, com o envio do seu nome e e-mail à New Value, parceira que opera o clube. Ela não
            recebe seu currículo nem suas candidaturas.
            <button type="button" class="club-link club-link--blue club-link--inline" @click="privacyOpen = true">Como o clube usa seus dados</button>
          </p>
        </div>
      </section>

      <!-- Modo gratuito: a mesma copy antes e depois do aceite -->
      <section v-else class="club-hero">
        <div class="club-circle club-circle--lg">
          <feather-icon icon="GiftIcon" svgClasses="w-7 h-7" />
        </div>
        <div class="club-hero__text">
          <div class="club-hero__title">
            <h3>Clube Recrutei</h3>
            <span v-if="active" class="club-badge">Ativo</span>
          </div>
          <p>
            Procurar emprego também pesa no bolso. Por isso, a Recrutei dá a quem tem cadastro aqui descontos em
            cursos, idiomas, terapia, cinema e lojas de mais de 490 parceiros. É de graça e vale para você,
            contratado ou não.
          </p>
          <p class="club-note">
            <feather-icon icon="InfoIcon" svgClasses="w-4 h-4" />
            <span>
              O Clube Recrutei é oferecido pela Recrutei, a plataforma onde você se candidata. Não é um benefício das
              empresas que anunciam vagas aqui, e usar ou não usar não muda nada nos seus processos seletivos.
            </span>
          </p>
          <div v-if="active" class="pt-2">
            <button class="club-btn" :disabled="opening" @click="openClub(null)">
              Abrir o site do clube
              <feather-icon icon="ExternalLinkIcon" svgClasses="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      <!-- Antes do aceite: nenhuma marca, só o que o clube oferece. No teste grátis, só o card acima. -->
      <template v-if="!showcase">
        <template v-if="!isTrial">
        <section class="flex flex-col gap-6">
          <h3 class="club-section-title">O que o clube tem para você</h3>
          <div class="club-tiles">
            <div v-for="tile in tiles" :key="tile.title" class="club-tile">
              <div class="club-circle">
                <feather-icon :icon="tile.icon" svgClasses="w-5 h-5" />
              </div>
              <div>
                <h4>{{ tile.title }}</h4>
                <p>{{ tile.text }}</p>
              </div>
            </div>
          </div>
        </section>

        <section class="club-consent">
          <h4>
            <feather-icon :icon="isSubscription ? 'EyeIcon' : 'LockIcon'" svgClasses="w-4 h-4" />
            {{ isSubscription ? "Veja as ofertas antes de decidir" : "Antes de ativar" }}
          </h4>
          <!-- Diz logo que usar é por assinatura (sem preço: ele fica no paywall), para ninguém se
               sentir enganado depois, e deixa claro que olhar não custa nada nem compromete. -->
          <template v-if="isSubscription">
            <p>
              Olhar as ofertas é livre e não compromete nada. Para usar um desconto, você assina o clube, e só se
              valer a pena para você.
            </p>
            <p class="club-consent__small">
              Para mostrar primeiro o que combina com você, usamos sua cidade e sua área de interesse. Se você assinar,
              a Recrutei envia seu nome e e-mail à New Value, empresa parceira que opera o clube. Ela não recebe seu
              currículo nem suas candidaturas, e as empresas que anunciam vagas não ficam sabendo se você usa o clube.
            </p>
          </template>
          <p v-else>
            Para liberar os descontos, a Recrutei envia seu nome e e-mail à New Value, empresa parceira que opera o
            Clube. Também usamos sua cidade e sua área de interesse para mostrar primeiro as ofertas que combinam com
            você. A New Value não recebe seu currículo nem suas candidaturas, e as empresas que anunciam vagas não
            ficam sabendo se você usa o Clube.
          </p>
          <label class="club-check">
            <input v-model="consent" type="checkbox" />
            <span v-if="isSubscription">
              Concordo com o uso do meu perfil para sugerir ofertas e, se eu assinar, com o envio do meu nome e e-mail à
              New Value.
            </span>
            <span v-else>Concordo com o envio do meu nome e e-mail à New Value e com o uso do meu perfil para sugerir ofertas.</span>
          </label>
          <p v-if="error" class="club-error">{{ error }}</p>
          <div class="club-consent__actions">
            <button class="club-btn club-btn--primary" :disabled="!consent || activating" @click="activate">
              {{ activating ? "Liberando..." : isSubscription ? "Ver as ofertas" : "Ativar meus descontos" }}
            </button>
            <button type="button" class="club-link club-link--blue" @click="privacyOpen = true">Como o clube usa seus dados</button>
          </div>
          <p v-if="isSubscription" class="club-consent__small">Ver as ofertas não gera cobrança. Você decide depois.</p>
        </section>
        </template>
      </template>

      <!-- Depois do aceite: a vitrine -->
      <template v-else>
        <section ref="showcase" class="flex flex-col gap-6">
          <div class="club-filters">
            <div class="club-input">
              <feather-icon icon="SearchIcon" svgClasses="w-5 h-5" />
              <input v-model="search" type="text" placeholder="Buscar por loja ou desconto" @input="onSearch" />
            </div>
            <div class="club-region">
              <span class="club-region__icon"><feather-icon icon="MapPinIcon" svgClasses="w-5 h-5" /></span>
              <v-select
                v-model="state"
                :options="stateOptions"
                :reduce="(option) => option.value"
                :clearable="false"
                placeholder="Região"
                class="club-region__select"
                @input="reload"
              >
                <template #no-options>Nenhum estado com esse nome</template>
              </v-select>
            </div>
          </div>

          <div class="club-chips">
            <button :class="['club-chip', { 'is-active': !category }]" @click="setCategory(null)">Todas</button>
            <button
              v-for="c in categories"
              :key="c.id"
              :class="['club-chip', { 'is-active': category === c.id }]"
              @click="setCategory(c.id)"
            >
              {{ c.name }}
            </button>
          </div>

          <p class="club-count">
            <template v-if="loadingOffers && !offers.length">Buscando ofertas...</template>
            <template v-else>Mostrando {{ offers.length }} de {{ total }} ofertas</template>
          </p>

          <p v-if="error" class="club-error">{{ error }}</p>

          <div v-if="offers.length" class="club-grid">
            <article v-for="offer in offers" :key="offer.id" class="club-card" @click="selected = offer">
              <div class="club-card__logo">
                <img
                  v-if="offer.logo_url"
                  :src="offer.logo_url"
                  :alt="offer.partner"
                  loading="lazy"
                  @error="useFullLogo($event, offer)"
                />
              </div>
              <div class="club-card__body">
                <span class="club-card__partner">{{ offer.partner }}</span>
                <h4>{{ offer.title }}</h4>
                <div class="club-tags">
                  <span class="club-tag">
                    <feather-icon :icon="typeIcon(offer.type)" svgClasses="w-4 h-4" />{{ typeLabel(offer.type) }}
                  </span>
                  <span class="club-tag">
                    <feather-icon icon="MapPinIcon" svgClasses="w-4 h-4" />{{ offer.location_label }}
                  </span>
                </div>
                <button class="club-btn club-btn--block">Ver oferta</button>
              </div>
            </article>
          </div>

          <p v-else-if="!loadingOffers" class="club-empty">
            Nenhuma oferta com esses filtros. Tente outra região ou categoria.
          </p>

          <div v-if="page < lastPage" class="flex justify-center">
            <button class="club-btn" :disabled="loadingOffers" @click="loadMore">
              {{ loadingOffers ? "Carregando..." : "Carregar mais ofertas" }}
            </button>
          </div>
        </section>

        <p v-if="mode === 'free' || (isTrial && trialStatus === 'active')" class="club-deactivate">
          Não quer mais o Clube Recrutei?
          <button @click="confirmDeactivate = true">Desativar meus descontos</button>
        </p>
      </template>
    </template>

    <!-- Detalhe da oferta -->
    <div v-if="selected" class="club-overlay club-overlay--center" @click.self="selected = null">
      <div class="club-dialog club-dialog--detail">
        <div class="club-detail__head">
          <div class="club-dialog__logo club-dialog__logo--sm">
            <img v-if="selected.logo_url" :src="selected.logo_full_url || selected.logo_url" :alt="selected.partner" />
          </div>
          <div class="club-detail__heading">
            <span class="club-card__partner">{{ selected.partner }}</span>
            <h3>{{ selected.title }}</h3>
            <div class="club-tags">
              <span class="club-tag">
                <feather-icon :icon="typeIcon(selected.type)" svgClasses="w-4 h-4" />{{ typeLabel(selected.type) }}
              </span>
              <span class="club-tag"><feather-icon icon="MapPinIcon" svgClasses="w-4 h-4" />{{ selected.location_label }}</span>
            </div>
          </div>
          <button class="club-close" aria-label="Fechar" @click="selected = null">
            <feather-icon icon="XIcon" svgClasses="w-6 h-6" />
          </button>
        </div>
        <!-- Os textos da New Value são longos: rolam por dentro, cabeçalho e botão ficam fixos. -->
        <div class="club-detail__scroll">
          <h4>Como usar</h4>
          <p v-if="canUse" class="club-first-step">
            Clique em Usar no site do clube. Ele abre numa nova aba, já com você conectado. Lá, siga estes passos:
          </p>
          <p v-else class="club-first-step">
            Assine o Clube de Benefícios Recrutei. Depois, o site do clube abre numa nova aba, já com você conectado.
            Lá, siga estes passos:
          </p>
          <!-- Texto da New Value: fala dos botões do site deles ("Gerar Cupom"). -->
          <p v-for="(line, i) in lines(selected.how_to_use)" :key="'h' + i" class="club-dialog__text">{{ line }}</p>
          <template v-if="selected.description">
            <h4 class="pt-2">Sobre a oferta</h4>
            <p v-for="(line, i) in lines(selected.description)" :key="'d' + i" class="club-dialog__text">{{ line }}</p>
          </template>
        </div>
        <div class="club-detail__actions">
          <button
            v-if="canUse"
            class="club-btn club-btn--primary club-btn--block"
            :disabled="opening"
            @click="openClub(selected)"
          >
            {{ opening ? "Abrindo..." : "Usar no site do clube" }}
            <feather-icon icon="ExternalLinkIcon" svgClasses="w-5 h-5" />
          </button>
          <p v-if="canUse && isTrial && trialStatus === 'available'" class="club-dialog__foot">
            Grátis. Seus {{ trialDays }} dias de teste começam agora.
          </p>
          <template v-else-if="!canUse">
            <button class="club-btn club-btn--primary club-btn--block" @click="openPaywall(selected)">
              Assinar para usar esta oferta
            </button>
            <p class="club-dialog__foot">Cancele quando quiser</p>
          </template>
          <p class="club-dialog__foot">
            Oferta de {{ selected.partner }} para quem tem cadastro na Recrutei. O parceiro não recebe nada sobre suas
            candidaturas.
          </p>
        </div>
      </div>
    </div>

    <!-- Aviso de nova aba -->
    <div v-if="opened" class="club-overlay" @click.self="opened = null">
      <div class="club-dialog club-dialog--center">
        <div class="club-dialog__top club-dialog__top--end">
          <button class="club-close" aria-label="Fechar" @click="opened = null">
            <feather-icon icon="XIcon" svgClasses="w-6 h-6" />
          </button>
        </div>
        <div class="club-circle club-circle--md mx-auto">
          <feather-icon icon="ExternalLinkIcon" svgClasses="w-6 h-6" />
        </div>
        <h3 class="club-dialog__title">O clube abriu numa nova aba</h3>
        <p class="club-dialog__text">
          Você já entra conectado.
          <template v-if="opened.partner">
            Lá, procure {{ opened.partner }} e gere seu cupom ou voucher.
          </template>
        </p>
        <p v-if="opened.trialStarted" class="club-dialog__text">
          Seu teste grátis começou hoje e vai até {{ formatDate(trial.ends_at) }}. Mandamos os detalhes para o seu e-mail.
        </p>
        <button class="club-btn club-btn--primary club-btn--block" @click="opened = null">Voltar às ofertas</button>
        <p class="club-dialog__foot">
          A aba não abriu?
          <a :href="opened.url" target="_blank" rel="noopener">Abrir de novo</a>
        </p>
      </div>
    </div>

    <!-- Paywall do clube: o preço só aparece aqui -->
    <div v-if="paywall" class="club-overlay club-overlay--center" @click.self="closePaywall">
      <div class="club-dialog club-paywall">
        <billing-step
          v-if="billingInfo"
          :info="billingInfo"
          plan="club"
          source="club_paywall"
          @back="billingInfo = null"
          @done="onBillingDone"
        />
        <template v-else>
          <div class="club-dialog__top">
            <span class="club-pill">Clube de Benefícios Recrutei</span>
            <button class="club-close" aria-label="Fechar" @click="closePaywall">
              <feather-icon icon="XIcon" svgClasses="w-6 h-6" />
            </button>
          </div>
          <h3 class="club-dialog__title club-paywall__title">Descontos que ajudam na sua carreira e no seu bolso</h3>
          <div v-if="paywall.offer" class="club-paywall__offer">
            <div class="club-paywall__offer-logo">
              <img
                v-if="paywall.offer.logo_url"
                :src="paywall.offer.logo_url"
                :alt="paywall.offer.partner"
                @error="useFullLogo($event, paywall.offer)"
              />
            </div>
            <div>
              <span>Você escolheu</span>
              <strong>{{ paywall.offer.partner }} · {{ paywall.offer.title }}</strong>
            </div>
          </div>
          <ul class="club-paywall__benefits">
            <li v-for="b in benefits" :key="b.title">
              <span class="club-circle club-circle--sm"><feather-icon :icon="b.icon" svgClasses="w-4 h-4" /></span>
              <div>
                <p class="club-paywall__benefit-title">{{ b.title }}</p>
                <p class="club-paywall__benefit-text">{{ b.text }}</p>
              </div>
            </li>
          </ul>
          <div class="club-paywall__plan">
            <p><strong>{{ priceLabel }}</strong> /mês</p>
            <span>Cobrança mensal no cartão. Cancela quando quiser e usa até o fim do mês pago.</span>
          </div>
          <p class="club-paywall__notice">
            <feather-icon icon="LockIcon" svgClasses="w-4 h-4" />
            <span>
              Ao assinar, a Recrutei envia seu nome e e-mail à New Value, parceira que opera o clube, para você entrar
              lá já conectado. Ela não recebe seu currículo nem suas candidaturas.
              <button type="button" class="club-link club-link--blue club-link--inline" @click="privacyOpen = true">Saiba mais</button>
            </span>
          </p>
          <p v-if="paywallError" class="club-error">{{ paywallError }}</p>
          <button
            class="club-btn club-btn--primary club-btn--block club-btn--lg"
            :disabled="checkoutLoading"
            @click="startCheckout"
          >
            {{ checkoutLoading ? "Abrindo o pagamento..." : `Assinar por ${priceLabel}/mês` }}
          </button>
          <p class="club-dialog__foot">Pagamento seguro pela Stripe. Cartão de crédito.</p>
          <button class="club-link club-link--center" @click="closePaywall">Agora não</button>
        </template>
      </div>
    </div>

    <!-- Volta do pagamento -->
    <div v-if="returning" class="club-overlay club-overlay--center">
      <div class="club-dialog club-dialog--center">
        <template v-if="returning === 'pending'">
          <spinner />
          <h3 class="club-dialog__title">Confirmando seu pagamento...</h3>
          <p class="club-dialog__text">Leva só alguns segundos.</p>
        </template>
        <template v-else-if="returning === 'paid'">
          <div class="club-dialog__top club-dialog__top--end">
            <button class="club-close" aria-label="Fechar" @click="returning = null">
              <feather-icon icon="XIcon" svgClasses="w-6 h-6" />
            </button>
          </div>
          <div class="club-circle club-circle--md club-circle--success mx-auto">
            <feather-icon icon="CheckIcon" svgClasses="w-6 h-6" />
          </div>
          <h3 class="club-dialog__title">Pronto, você é assinante do Clube de Benefícios Recrutei</h3>
          <p class="club-dialog__text">
            Seus descontos já estão liberados.
            <template v-if="returnOffer">Abra {{ returnOffer.partner }} na lista e clique em Usar no site do clube.</template>
            <template v-else>Escolha uma oferta na lista e clique em Usar no site do clube.</template>
          </p>
          <button class="club-btn club-btn--primary club-btn--block club-btn--lg" @click="showBenefits">
            Ver benefícios
          </button>
          <p class="club-dialog__foot">O recibo chega no seu e-mail.</p>
        </template>
        <template v-else>
          <h3 class="club-dialog__title">Não conseguimos confirmar o pagamento</h3>
          <p class="club-dialog__text">
            Se a cobrança aparecer no seu cartão, a assinatura é liberada em alguns minutos. Se não, tente de novo.
          </p>
          <button class="club-btn club-btn--block" @click="returning = null">Fechar</button>
        </template>
      </div>
    </div>

    <!-- Termo de uso dos dados no clube: só a finalidade do clube, sem mandar a pessoa para a
         política inteira. Segue a política da Recrutei (itens 4 a 8 e 10) e linka para ela no fim. -->
    <div v-if="privacyOpen" class="club-overlay club-overlay--center club-overlay--top" @click.self="privacyOpen = false">
      <div class="club-dialog club-privacy">
        <div class="club-dialog__top">
          <h3 class="club-dialog__title club-privacy__title">Como o Clube Recrutei usa seus dados</h3>
          <button class="club-close" aria-label="Fechar" @click="privacyOpen = false">
            <feather-icon icon="XIcon" svgClasses="w-6 h-6" />
          </button>
        </div>
        <div class="club-privacy__body">
          <h4>Quem cuida dos seus dados</h4>
          <p>
            A Recrutei (Peply Tecnologia em Recursos Humanos Ltda., CNPJ 32.002.308/0001-62, Uberlândia/MG). Dúvidas ou
            pedidos sobre seus dados: privacidade@recrutei.com.br.
          </p>

          <h4>Para que usamos</h4>
          <ul>
            <li>
              Mostrar as ofertas do clube e colocar primeiro as que combinam com você, usando sua cidade e sua área de
              interesse. Base legal: o seu aceite (consentimento).
            </li>
            <li v-if="isSubscription">
              Criar seu acesso ao site do clube e cobrar a assinatura, se você assinar. Base legal: o contrato da
              assinatura e o seu aceite.
            </li>
            <li v-else>Criar seu acesso ao site do clube, já conectado. Base legal: o seu aceite.</li>
          </ul>

          <h4>O que vai para quem</h4>
          <ul>
            <li>
              <strong>New Value</strong>, empresa parceira que opera o clube: seu nome, sobrenome, e-mail e um código de
              identificação{{ isSubscription ? ", só depois que você assina" : isTrial ? ", só quando você usa a primeira oferta" : "" }}. Nunca seu currículo, suas
              candidaturas, seu CPF ou seu telefone.
            </li>
            <li v-if="mode !== 'free'">
              <strong>Stripe</strong>, que processa o pagamento: nome, e-mail, endereço e CPF, para a cobrança e a nota
              fiscal. Os dados do cartão ficam só com a Stripe; a Recrutei não vê nem guarda.
            </li>
            <li><strong>Empresas que anunciam vagas</strong>: nada. Elas não ficam sabendo se você usa o clube.</li>
          </ul>

          <h4>O que não muda</h4>
          <p>
            O clube é opcional e separado dos processos seletivos. Usar ou não usar, assinar ou não assinar, não muda
            nada nas suas candidaturas. Nenhuma empresa nem a Recrutei cobra qualquer valor para você participar de um
            processo seletivo.
          </p>

          <h4>Por quanto tempo</h4>
          <p>
            Seu cadastro na New Value existe enquanto o clube estiver ativo para você.
            <template v-if="isSubscription">Quando a assinatura termina</template><template v-else-if="isTrial">Quando o teste termina sem assinatura, ou quando você desativa</template><template v-else>Quando você desativa</template>,
            pedimos à New Value que apague esse cadastro. O registro do seu aceite<template v-if="isSubscription"> e dos
            pagamentos</template> fica guardado pelo tempo que a lei exige. O resto segue o prazo da nossa política de
            privacidade.
          </p>

          <h4>Seus direitos</h4>
          <p>
            Você pode, a qualquer momento, ver e corrigir seus dados no seu perfil, retirar o aceite
            <template v-if="isSubscription">(cancelando a assinatura em Gerenciar assinatura)</template><template v-else>(em Desativar meus descontos)</template>
            e pedir a exclusão dos seus dados pelo privacidade@recrutei.com.br. Respondemos em até 72 horas úteis.
          </p>

          <h4>Segurança</h4>
          <p>
            Seus dados ficam em servidores protegidos, com acesso restrito, e vão para a New Value e para a Stripe por
            conexão criptografada.
          </p>
        </div>
        <div class="club-privacy__foot">
          <a href="https://recrutei.com.br/privacidade-e-seguranca" target="_blank" rel="noopener">
            Ler a política de privacidade completa da Recrutei
          </a>
          <button type="button" class="club-btn club-btn--primary" @click="privacyOpen = false">Entendi</button>
        </div>
      </div>
    </div>

    <!-- Confirmação de desativar -->
    <div v-if="confirmDeactivate" class="club-overlay" @click.self="confirmDeactivate = false">
      <div class="club-dialog club-dialog--center">
        <h3 class="club-dialog__title">Desativar o Clube Recrutei?</h3>
        <p class="club-dialog__text">
          Seu cadastro no site do clube é apagado, junto com os cupons e vouchers que estão lá. Você pode ativar de
          novo quando quiser.
        </p>
        <button class="club-btn club-btn--danger club-btn--block" :disabled="deactivating" @click="deactivate">
          {{ deactivating ? "Desativando..." : "Desativar" }}
        </button>
        <button class="club-btn club-btn--block" @click="confirmDeactivate = false">Cancelar</button>
      </div>
    </div>
  </div>
</template>

<script>
import Spinner from "@/components/spinner/Spinner.vue";
import vSelect from "vue-select";
import BillingStep from "@/components/public-profile/BillingStep.vue";
import publicProfile from "@/services/publicProfile";
import { trackEvent } from "@/services/posthog";
import {
  fetchClubStatus,
  trackOfferEvent,
  activateClub,
  fetchClubOffers,
  openClubAccess,
  deactivateClub,
  startClubCheckout,
  fetchCheckoutStatus,
  clubBillingPortal,
  errorMessage,
  errorReason,
} from "@/services/club";

// Oferta escolhida antes de ir para a Stripe: na volta, o aviso diz o que procurar no clube.
const PENDING_OFFER_KEY = "@recrutei:club:pending_offer";

const TYPES = {
  voucher: { label: "Voucher", icon: "AwardIcon" },
  coupon: { label: "Cupom", icon: "TagIcon" },
  website: { label: "Link do site", icon: "LinkIcon" },
};

export default {
  name: "Club",
  components: { Spinner, vSelect, BillingStep },
  data: () => ({
    loading: true,
    active: false,
    mode: "free",
    consented: false,
    subscription: null,
    trial: null,
    price: null,
    paywall: null,
    paywallError: null,
    checkoutLoading: false,
    billingInfo: null,
    portalLoading: false,
    returning: null,
    returnOffer: null,
    privacyOpen: false,
    benefits: [
      { icon: "AwardIcon", title: "Cursos e idiomas", text: "Até 50% em escolas como Wizard e CCLI, e mais de 180 cursos" },
      { icon: "HeartIcon", title: "Saúde e bem-estar", text: "Até 40% em terapia online para atravessar a busca com calma" },
      { icon: "TagIcon", title: "Cultura e lazer", text: "Cinema, passeios e eventos com desconto" },
      { icon: "ShoppingBagIcon", title: "Compras", text: "Magalu, Samsung e outras lojas" },
      { icon: "GiftIcon", title: "Mais de 490 parceiros", text: "Ofertas novas todo mês, com as da sua região primeiro" },
    ],
    consent: false,
    activating: false,
    error: null,
    offers: [],
    categories: [],
    states: [],
    total: 0,
    page: 1,
    lastPage: 1,
    loadingOffers: false,
    search: "",
    state: null,
    category: null,
    selected: null,
    opened: null,
    opening: false,
    confirmDeactivate: false,
    deactivating: false,
    searchTimer: null,
    // Categorias raiz da New Value, sem citar marca antes do aceite.
    tiles: [
      { icon: "AwardIcon", title: "Ensino e idiomas", text: "Um curso ou idioma novo para o currículo, com mais de 180 opções" },
      { icon: "ShoppingBagIcon", title: "Compras", text: "Economia no dia a dia em lojas de eletrônicos, casa e moda" },
      { icon: "TagIcon", title: "Cultura e lazer", text: "Uma pausa na busca: cinema, passeios e eventos" },
      { icon: "HeartIcon", title: "Saúde e bem-estar", text: "Cuidar da cabeça também conta: até 40% em terapia online" },
    ],
  }),
  computed: {
    // No modo teste, quem assinou ou já passou do prazo segue as regras da assinatura.
    isSubscription() {
      return this.mode === "subscription" || (this.mode === "trial" && !this.isTrial);
    },
    isTrial() {
      return this.mode === "trial" && !this.subscribed && this.trialStatus !== "ended";
    },
    subscriptionStatus() {
      return (this.subscription && this.subscription.status) || "none";
    },
    subscribed() {
      return this.subscriptionStatus === "active" || this.subscriptionStatus === "past_due";
    },
    // available (não começou), active ou ended; null fora do modo teste.
    trialStatus() {
      return (this.trial && this.trial.status) || null;
    },
    trialDays() {
      return (this.trial && this.trial.days) || 30;
    },
    // Pode abrir o site do clube agora.
    canUse() {
      return this.active;
    },
    // Vitrine visível só depois do aceite, nos dois modos: nenhuma marca antes disso.
    showcase() {
      return this.consented;
    },
    priceLabel() {
      const cents = (this.price && this.price.monthly_cents) || 1990;
      return (cents / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    },
    stateOptions() {
      return [{ label: "Todas as regiões", value: "" }].concat(
        this.states.map((s) => ({ label: s.name, value: s.code }))
      );
    },
    // Onde o talento está no funil, para o PostHog.
    funnelStatus() {
      if (this.active) return "active";
      if (this.subscriptionStatus === "ended") return "ended";
      return this.consented ? "consented" : "visitor";
    },
    label() {
      return this.$route.params.company_label || localStorage.getItem("label") || "recrutei";
    },
  },
  watch: {
    selected(offer) {
      if (!offer) return;
      this.track("club_offer_viewed", this.offerProps(offer));
      trackOfferEvent(this.label, offer.id, "view");
    },
  },
  async created() {
    try {
      this.applyStatus(await fetchClubStatus(this.label));
      this.track("club_viewed", { from_checkout: !!this.$route.query.checkout });
      if (this.showcase) this.loadOffers();
    } catch (e) {
      this.error = errorMessage(e);
    } finally {
      this.loading = false;
    }
    this.handleCheckoutReturn();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    /**
     * Funil do clube no PostHog: todo evento leva o modo, o estado da assinatura e a
     * empresa do app, e a pessoa ganha `club_status` para coorte (assinante, desistiu no
     * checkout...). Só parceiro e oferta, nunca dado do talento além do que o PostHog já tem.
     */
    track(event, props = {}) {
      trackEvent(event, {
        club_mode: this.mode,
        club_status: this.funnelStatus,
        club_subscription_status: this.subscriptionStatus,
        club_trial_status: this.trialStatus,
        company_label: this.label,
        ...props,
      });
      try {
        if (window.posthog && typeof window.posthog.setPersonProperties === "function") {
          window.posthog.setPersonProperties({
            club_status: this.funnelStatus,
          });
        }
      } catch (_) {
        // no-op
      }
    },
    offerProps(offer) {
      if (!offer) return { offer_id: null, offer_partner: null };
      return { offer_id: offer.id, offer_partner: offer.partner, offer_type: offer.type, offer_featured: !!offer.featured };
    },
    // A versão reduzida do logo falhou (conversão que não existe lá): cai no original.
    useFullLogo(event, offer) {
      const full = offer && offer.logo_full_url;
      if (full && event.target.src !== full) event.target.src = full;
    },
    applyStatus(status) {
      if (!status) return;
      this.mode = status.mode || "free";
      this.active = !!status.active;
      // No teste grátis, `active` vem antes do aceite (quem ainda não começou já pode usar).
      this.consented = !!(status.consented || (status.active && status.mode !== "trial"));
      this.subscription = status.subscription || null;
      this.trial = status.trial || null;
      this.price = status.price || null;
    },
    formatDate(iso) {
      return iso ? new Date(iso).toLocaleDateString("pt-BR") : "";
    },
    openPaywall(offer) {
      this.selected = null;
      this.paywallError = null;
      this.billingInfo = null;
      this.paywall = { offer: offer || null };
      if (offer) trackOfferEvent(this.label, offer.id, "paywall");
      this.track("club_paywall_opened", { ...this.offerProps(offer), price_cents: this.price && this.price.monthly_cents });
    },
    closePaywall() {
      this.paywall = null;
      this.billingInfo = null;
    },
    async startCheckout() {
      this.checkoutLoading = true;
      this.paywallError = null;
      const offer = this.paywall && this.paywall.offer;
      try {
        const url = await startClubCheckout(this.label, offer);
        try {
          sessionStorage.setItem(PENDING_OFFER_KEY, JSON.stringify(offer ? { id: offer.id, partner: offer.partner } : null));
        } catch (_) {
          // Sem sessionStorage (aba anônima restrita): a volta só não cita a oferta.
        }
        this.track("club_checkout_started", { ...this.offerProps(offer), price_cents: this.price && this.price.monthly_cents });
        window.location.href = url;
      } catch (e) {
        this.track("club_checkout_blocked", { ...this.offerProps(offer), reason: errorReason(e) || "error" });
        if (errorReason(e) === "billing_required") {
          // Faltam CPF ou endereço para a nota: mesma etapa do perfil público, depois tenta de novo.
          try {
            this.billingInfo = await publicProfile.billingInfo();
          } catch (err) {
            this.paywallError = errorMessage(err);
          }
        } else {
          this.paywallError = errorMessage(e);
        }
        this.checkoutLoading = false;
      }
    },
    onBillingDone() {
      this.billingInfo = null;
      this.startCheckout();
    },
    async openPortal() {
      this.portalLoading = true;
      try {
        const url = await clubBillingPortal(this.label);
        this.track("club_billing_portal_opened");
        window.location.href = url;
      } catch (e) {
        this.error = errorMessage(e);
        this.portalLoading = false;
      }
    },
    // Volta da Stripe: confere o pagamento (a API dá baixa se o webhook ainda não chegou).
    async handleCheckoutReturn() {
      const { checkout, session_id: sessionId } = this.$route.query;
      if (!checkout) return;
      this.$router.replace({ query: {} }).catch(() => {});
      if (checkout !== "success" || !sessionId) return;
      try {
        this.returnOffer = JSON.parse(sessionStorage.getItem(PENDING_OFFER_KEY) || "null");
        sessionStorage.removeItem(PENDING_OFFER_KEY);
      } catch (_) {
        this.returnOffer = null;
      }
      this.returning = "pending";
      for (let attempt = 0; attempt < 15; attempt += 1) {
        try {
          const result = await fetchCheckoutStatus(this.label, sessionId);
          if (result.status === "paid") {
            this.applyStatus(result.club);
            this.returning = "paid";
            this.track("club_subscribed", this.offerProps(this.returnOffer));
            return;
          }
          if (result.status === "failed" || result.status === "expired") break;
        } catch (_) {
          // Falha de rede numa tentativa: tenta de novo na próxima volta do laço.
        }
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
      this.returning = "failed";
      this.track("club_checkout_failed", this.offerProps(this.returnOffer));
    },
    // Depois de pagar, volta para a vitrine: o talento escolhe a oferta aqui, não no site do clube.
    showBenefits() {
      this.returning = null;
      this.$nextTick(() => {
        const el = this.$refs.showcase;
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    },
    typeLabel(type) {
      return (TYPES[type] || TYPES.website).label;
    },
    typeIcon(type) {
      return (TYPES[type] || TYPES.website).icon;
    },
    lines(text) {
      return (text || "").split("\n").filter(Boolean);
    },
    async activate() {
      this.activating = true;
      this.error = null;
      try {
        this.applyStatus(await activateClub(this.label));
        this.track("club_consented");
        if (this.showcase) this.loadOffers();
      } catch (e) {
        this.error = errorMessage(e);
      } finally {
        this.activating = false;
      }
    },
    async loadOffers(append = false) {
      this.loadingOffers = true;
      this.error = null;
      const params = { page: this.page };
      // Sem `state` a API abre na região do talento; depois vale a escolha dele.
      if (this.state !== null) params.state = this.state;
      if (this.category) params.category = this.category;
      if (this.search.trim()) params.search = this.search.trim();
      try {
        const body = await fetchClubOffers(this.label, params);
        this.offers = append ? this.offers.concat(body.data) : body.data;
        this.total = body.meta.total;
        this.lastPage = body.meta.last_page;
        this.categories = body.filters.categories;
        this.states = body.filters.states;
        if (this.state === null) this.state = body.filters.selected_state || "";
      } catch (e) {
        this.error = errorMessage(e);
      } finally {
        this.loadingOffers = false;
      }
    },
    reload() {
      this.page = 1;
      this.loadOffers();
      // Busca já com debounce; sem o texto digitado, só se teve busca.
      this.track("club_filter_used", {
        filter_state: this.state || null,
        filter_category: this.category || null,
        filter_search: !!this.search.trim(),
      });
    },
    loadMore() {
      this.page += 1;
      this.loadOffers(true);
    },
    setCategory(id) {
      this.category = id;
      this.reload();
    },
    onSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.reload, 400);
    },
    async openClub(offer) {
      // A aba nasce no clique, senão o navegador bloqueia; o endereço chega depois.
      const tab = window.open("", "_blank");
      this.opening = true;
      try {
        const trialBefore = this.trialStatus;
        const { url, club } = await openClubAccess(this.label, offer && offer.id);
        if (tab) tab.location.href = url;
        this.error = null;
        this.applyStatus(club);
        this.track("club_offer_used", this.offerProps(offer));
        const trialStarted = trialBefore === "available" && this.trialStatus === "active";
        if (trialStarted) this.track("club_trial_started", this.offerProps(offer));
        this.selected = null;
        this.opened = { url, partner: offer ? offer.partner : null, trialStarted };
      } catch (e) {
        if (tab) tab.close();
        this.error = errorMessage(e);
        this.selected = null;
      } finally {
        this.opening = false;
      }
    },
    async deactivate() {
      this.deactivating = true;
      try {
        const status = await deactivateClub(this.label);
        this.track("club_deactivated");
        // No teste, os dias seguem contando e a vitrine continua: o próximo uso cadastra de novo.
        if (this.mode === "trial") {
          this.applyStatus(status);
          this.confirmDeactivate = false;
          return;
        }
        this.active = false;
        this.consent = false;
        this.offers = [];
        this.state = null;
        this.confirmDeactivate = false;
      } catch (e) {
        this.error = errorMessage(e);
        this.confirmDeactivate = false;
      } finally {
        this.deactivating = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$border: 0.5px solid #a8a8a8;
$primary: #4169e1;

.club {
  font-family: Roboto, sans-serif;
  color: #4f4f4f;
}
.club-loading {
  display: flex;
  justify-content: center;
  padding: 64px 0;
}
.club-hero {
  display: flex;
  gap: 24px;
  padding: 24px;
  border: $border;
  border-radius: 16px;
  @media (max-width: 576px) {
    flex-direction: column;
    gap: 16px;
  }
  &__text {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
    p {
      font-size: 16px;
      line-height: 18.75px;
    }
  }
  &__title {
    display: flex;
    align-items: center;
    gap: 8px;
    h3 {
      font-size: 20px;
      font-weight: 600;
      color: #333;
      margin: 0;
    }
  }
}
.club-note {
  display: flex;
  gap: 8px;
  padding-top: 8px;
  font-size: 14px !important;
  line-height: 18px !important;
  color: #696969;
}
.club-badge {
  padding: 4px 8px;
  border-radius: 4px;
  border: 0.5px solid #86efac;
  background: #dcfce7;
  color: #166534;
  font-size: 14px;
  font-weight: 500;
}
.club-circle {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 9999px;
  background: rgba(65, 105, 225, 0.18);
  color: $primary;
  display: flex;
  align-items: center;
  justify-content: center;
  &--lg {
    width: 56px;
    height: 56px;
  }
  &--md {
    width: 44px;
    height: 44px;
  }
}
.club-section-title {
  font-size: 18px;
  font-weight: 400;
  color: #4f4f4f;
  margin: 0;
}
.club-tiles {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
}
.club-tile {
  display: flex;
  gap: 16px;
  padding: 16px;
  border: $border;
  border-radius: 16px;
  h4 {
    font-size: 18px;
    font-weight: 500;
    color: #333;
    margin: 0 0 4px;
  }
  p {
    font-size: 16px;
    line-height: 18.75px;
  }
}
.club-consent {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  border-radius: 16px;
  background: rgba(65, 105, 225, 0.18);
  @media (max-width: 576px) {
    padding: 16px;
  }
  h4 {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 18px;
    font-weight: 500;
    color: #333;
    margin: 0;
  }
  p {
    font-size: 16px;
    line-height: 18.75px;
  }
  &__small {
    font-size: 14px !important;
    line-height: 18px !important;
    color: #696969;
  }
  &__actions {
    display: flex;
    align-items: center;
    gap: 24px;
    a {
      color: $primary;
      text-decoration: underline;
    }
    @media (max-width: 576px) {
      flex-direction: column;
      gap: 12px;
      .club-btn {
        width: 100%;
      }
    }
  }
}
.club-check {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  color: #333;
  font-size: 16px;
  cursor: pointer;
  input {
    width: 20px;
    height: 20px;
    margin-top: 1px;
    flex-shrink: 0;
    accent-color: #1551b1;
  }
}
.club-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 40px;
  padding: 8px 16px;
  border-radius: 8px;
  border: $border;
  background: #fff;
  color: #4f4f4f;
  font-family: Roboto, sans-serif;
  font-size: 16px;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: #e7edf0;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  &--primary {
    background: $primary;
    border: none;
    color: #fff;
    &:hover:not(:disabled) {
      background: #1e3a8a;
    }
  }
  &--danger {
    background: #de2348;
    border: none;
    color: #fff;
    &:hover:not(:disabled) {
      background: #b51c3a;
    }
  }
  &--block {
    width: 100%;
  }
}
.club-filters {
  display: flex;
  gap: 16px;
  @media (max-width: 576px) {
    flex-direction: column;
    gap: 8px;
  }
}
.club-input {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 8px 12px;
  border: $border;
  border-radius: 8px;
  flex: 1;
  color: #696969;
  input,
  select {
    border: none;
    outline: none;
    flex: 1;
    font-size: 16px;
    color: #4f4f4f;
    background: transparent;
    font-family: Roboto, sans-serif;
  }
}
// Mesmo visual do select de Candidaturas da aba Vagas (.applications-scope-vselect).
.club-region {
  position: relative;
  flex: 0 0 260px;
  @media (max-width: 576px) {
    flex: 1;
  }
  &__icon {
    position: absolute;
    left: 12px;
    top: 10px;
    z-index: 2;
    display: flex;
    color: #696969;
    pointer-events: none;
  }
}
.club-region__select {
  font-family: Roboto, sans-serif;
  font-size: 16px;
  color: #4f4f4f;
  ::v-deep .vs__dropdown-toggle {
    height: 40px;
    padding: 0 4px 0 36px;
    border-radius: 8px;
    border: 0.5px solid #a8a8a8;
    background: #fff;
    cursor: pointer;
  }
  ::v-deep .vs__selected {
    color: #4f4f4f;
    font-size: 16px;
    margin: 0;
    padding: 0;
  }
  ::v-deep .vs__search,
  ::v-deep .vs__search:focus {
    margin: 0;
    padding: 0;
    font-size: 16px;
  }
  ::v-deep .vs__open-indicator {
    fill: #4f4f4f;
  }
  &.vs--open ::v-deep .vs__dropdown-toggle,
  &:hover ::v-deep .vs__dropdown-toggle {
    border-color: #4169e1;
  }
  ::v-deep .vs__dropdown-menu {
    border: 0.5px solid #a8a8a8;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    padding: 4px;
    margin-top: 4px;
    max-height: 280px;
  }
  ::v-deep .vs__dropdown-option {
    border-radius: 6px;
    padding: 10px 12px;
    font-size: 14px;
    color: #333;
    white-space: normal;
  }
  ::v-deep .vs__dropdown-option--highlight {
    background: #4169e1;
    color: #fff;
  }
  ::v-deep .vs__dropdown-option--selected {
    background: #c8e0ff;
    color: #1e3a8a;
  }
  ::v-deep .vs__dropdown-option--selected.vs__dropdown-option--highlight {
    background: #4169e1;
    color: #fff;
  }
}
.club-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  @media (max-width: 576px) {
    flex-wrap: nowrap;
    overflow-x: auto;
  }
}
.club-chip {
  flex-shrink: 0;
  padding: 4px 8px;
  border-radius: 4px;
  border: $border;
  background: #fff;
  color: #333;
  font-size: 14px;
  cursor: pointer;
  &.is-active {
    border-color: #1551b1;
    background: rgba(21, 81, 177, 0.18);
    color: #1551b1;
    font-weight: 500;
  }
}
.club-count {
  font-size: 16px;
}
.club-error {
  color: #de2348;
  font-size: 14px;
}
.club-empty {
  text-align: center;
  padding: 32px 0;
}
.club-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  @media (max-width: 992px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}
.club-card {
  display: flex;
  flex-direction: column;
  border: $border;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  &:hover {
    box-shadow: 0 3px 5px 0 rgba(9, 30, 66, 0.2), 0 0 1px 0 rgba(9, 30, 66, 0.31);
  }
  &__logo {
    height: 104px;
    padding: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-bottom: $border;
    img {
      max-width: 150px;
      max-height: 72px;
      object-fit: contain;
    }
  }
  &__body {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px;
    flex: 1;
    h4 {
      font-size: 18px;
      font-weight: 500;
      line-height: 21.09px;
      color: #333;
      margin: 0;
      flex: 1;
    }
  }
  &__partner {
    font-size: 14px;
    color: #696969;
  }
}
.club-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.club-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 4px;
  border: $border;
  color: #333;
  font-size: 14px;
}
.club-badge--muted {
  border-color: #a8a8a8;
  background: #f6f6f6;
  color: #696969;
}
.club-hero__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  padding-top: 8px;
  @media (max-width: 576px) {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    .club-btn {
      width: 100%;
    }
  }
}
.club-hero__meta {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #696969;
}
.club-link {
  padding: 0;
  border: none;
  background: none;
  color: #333;
  font-family: Roboto, sans-serif;
  font-size: 14px;
  text-decoration: underline;
  cursor: pointer;
  &--center {
    align-self: center;
  }
}
.club-link--blue {
  color: #4169e1;
}
.club-link--inline {
  font-size: 13px;
  margin-left: 2px;
}
.club-overlay--top {
  z-index: 50010;
}
.club-privacy {
  max-width: 600px;
  max-height: min(720px, calc(100vh - 48px));
  padding: 0;
  gap: 0;
  overflow: hidden;
  .club-dialog__top {
    padding: 20px 20px 12px;
    border-bottom: 0.5px solid #a8a8a8;
  }
  &__title {
    margin: 0 !important;
    font-size: 20px !important;
  }
  &__body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 16px 20px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    font-size: 14px;
    line-height: 20px;
    color: #4f4f4f;
    h4 {
      margin: 8px 0 0;
      font-size: 15px;
      font-weight: 600;
      color: #333;
    }
    ul {
      margin: 0;
      padding-left: 18px;
      list-style: disc;
      li {
        margin-bottom: 4px;
      }
    }
  }
  &__foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 20px 20px;
    border-top: 0.5px solid #a8a8a8;
    a {
      font-size: 13px;
      color: #696969;
      text-decoration: underline;
    }
    @media (max-width: 576px) {
      flex-direction: column-reverse;
      align-items: stretch;
    }
  }
}
.club-pill {
  padding: 4px 8px;
  border-radius: 4px;
  background: #4169e1;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
}
.club-circle--sm {
  width: 32px;
  height: 32px;
  background: rgba(65, 105, 225, 0.1);
}
.club-circle--success {
  background: #ceedce;
  color: #32cd32;
}
.club-btn--lg {
  height: 48px;
  font-weight: 600;
}
.club-paywall__notice {
  display: flex;
  gap: 8px;
  font-size: 13px;
  line-height: 18px;
  color: #4f4f4f;
}
.club-check--sm {
  font-size: 13px;
  line-height: 18px;
  color: #4f4f4f;
}
// Mesmo desenho do paywall do perfil público (PaywallDialog), na cor do clube.
.club-paywall {
  max-width: 540px;
  &__title {
    margin: 0;
  }
  &__offer {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 12px 8px 8px;
    border-radius: 8px;
    background: #f6f6f6;
    span {
      display: block;
      font-size: 13px;
      color: #696969;
    }
    strong {
      font-size: 15px;
      font-weight: 500;
      color: #333;
    }
  }
  &__offer-logo {
    flex-shrink: 0;
    padding: 4px;
    border-radius: 6px;
    background: #fff;
    img {
      display: block;
      max-width: 64px;
      max-height: 32px;
      object-fit: contain;
    }
  }
  &__benefits {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin: 0;
    padding: 0;
    list-style: none;
    li {
      display: flex;
      align-items: flex-start;
      gap: 12px;
    }
  }
  &__benefit-title {
    font-size: 16px;
    font-weight: 500;
    color: #333;
  }
  &__benefit-text {
    font-size: 14px;
    color: #696969;
  }
  &__plan {
    padding: 14px 16px;
    border: 1.5px solid #4169e1;
    border-radius: 8px;
    p {
      font-size: 14px;
      color: #696969;
    }
    strong {
      font-size: 24px;
      font-weight: 600;
      color: #333;
    }
    span {
      font-size: 13px;
      color: #696969;
    }
  }
}
.club-deactivate {
  font-size: 14px;
  color: #696969;
  button {
    border: none;
    background: none;
    color: #333;
    text-decoration: underline;
    cursor: pointer;
    font-size: 14px;
  }
}
.club-overlay {
  position: fixed;
  inset: 0;
  z-index: 50000;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 80px 16px 24px;
  overflow-y: auto;
  @media (max-width: 576px) {
    padding: 24px 16px;
  }
}
.club-dialog {
  width: 100%;
  max-width: 560px;
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  @media (max-width: 576px) {
    padding: 20px;
  }
  &--center {
    text-align: center;
  }
  hr {
    border: none;
    border-top: 0.5px solid #a8a8a8;
    margin: 0;
  }
  h4 {
    font-size: 18px;
    font-weight: 500;
    color: #333;
    margin: 0;
  }
  &__top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    &--end {
      justify-content: flex-end;
    }
  }
  &__logo {
    border: $border;
    border-radius: 8px;
    padding: 8px;
    img {
      max-width: 144px;
      max-height: 72px;
      object-fit: contain;
      display: block;
    }
  }
  &__title {
    font-size: 22px;
    font-weight: 600;
    color: #333;
    margin: -12px 0 0;
  }
  &--center &__title {
    margin: 0;
  }
  &__text {
    font-size: 16px;
    line-height: 20px;
  }
  &__foot {
    font-size: 14px;
    color: #696969;
    text-align: center;
    a {
      color: #333;
      text-decoration: underline;
    }
  }
}
.club-close {
  border: none;
  background: none;
  color: #4f4f4f;
  cursor: pointer;
  padding: 0;
}
.club-first-step {
  font-size: 16px;
  line-height: 20px;
  color: #333;
  padding: 12px;
  border-radius: 8px;
  background: rgba(65, 105, 225, 0.1);
}
.club-overlay--center {
  align-items: center;
  padding: 24px 16px;
}
.club-dialog--detail {
  max-width: 520px;
  max-height: min(640px, calc(100vh - 48px));
  padding: 0;
  gap: 0;
  overflow: hidden;
}
.club-detail__head {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  padding: 20px 20px 16px;
  border-bottom: 0.5px solid #a8a8a8;
}
.club-detail__heading {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  h3 {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin: 0 0 4px;
  }
}
.club-dialog__logo--sm {
  flex-shrink: 0;
  padding: 6px;
  img {
    max-width: 88px;
    max-height: 44px;
  }
}
.club-detail__scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  .club-dialog__text {
    font-size: 14px;
    line-height: 19px;
  }
  .club-first-step {
    font-size: 14px;
    line-height: 19px;
  }
}
.club-detail__actions {
  padding: 16px 20px 20px;
  border-top: 0.5px solid #a8a8a8;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
</style>
